import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";

const ui = {
  primary: "#009db9",
  primaryDark: "#007a8a",
  text: "#1f2933",
  border: "#e5e7eb",
  radius: "16px",
  shadow: "0 4px 20px rgba(16, 24, 40, 0.06)",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#fff",
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: ui.primary },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: ui.primary,
    },
    "&.Mui-disabled": { backgroundColor: "#f3f5f7" },
  },
  "& label.Mui-focused": { color: ui.primary },
};

const primaryButtonSx = {
  backgroundColor: ui.primary,
  color: "#fff",
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 700,
  px: 3,
  py: 1.25,
  boxShadow: "none",
  "&:hover": { backgroundColor: ui.primaryDark, boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: "#d5dbe0", color: "#8a949e" },
};

const selectMenuProps = { PaperProps: { sx: { maxHeight: 320 } } };

const SectionCard = ({ title, action, children }) => (
  <Card
    sx={{
      mb: { xs: 2.5, md: 4 },
      borderRadius: ui.radius,
      border: `1px solid ${ui.border}`,
      backgroundColor: "#fff",
      boxShadow: ui.shadow,
      overflow: "visible",
    }}
  >
    <CardContent
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        "&:last-child": { pb: { xs: 2, sm: 3, md: 4 } },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          gap: 2,
          mb: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: ui.text,
            pl: 1.5,
            borderLeft: `4px solid ${ui.primary}`,
            lineHeight: 1.3,
          }}
        >
          {title}
        </Typography>
        {action}
      </Box>
      {children}
    </CardContent>
  </Card>
);

const CREATE_ROLE_ORDER = [
  ["EMPLOYEE", "Employee"],
  ["CONSULTANT", "Consultant"],
  ["ADMIN", "Admin"],
  ["FINANCIAL_ADMIN", "Financial Admin"],
  ["INTERN", "Intern"],
  ["MANAGER", "Manager"],
];

const CreateUserForm = ({
  fullName,
  setFullName,
  employeeId,
  setEmployeeId,
  email,
  setEmail,
  role,
  setRole,
  department,
  setDepartment,
  jobTitle,
  setJobTitle,
  departmentOptions,
  submitting,
  handleSubmit,
}) => {
  const canSubmit =
    !!fullName &&
    !!employeeId &&
    !!email &&
    !!role &&
    !!department &&
    !!jobTitle &&
    !submitting;

  return (
    <SectionCard title="Create new user">
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            sm: "repeat(2, minmax(0, 1fr))",
          },
          gap: 2.5,
        }}
      >
        <TextField
          label="Name*"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          variant="outlined"
          fullWidth
          sx={fieldSx}
          disabled={submitting}
        />
        <TextField
          label="Employee-ID*"
          value={employeeId}
          onChange={(e) => setEmployeeId(e.target.value)}
          variant="outlined"
          fullWidth
          sx={fieldSx}
          disabled={submitting}
        />
        <TextField
          label="Email*"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          variant="outlined"
          fullWidth
          sx={fieldSx}
          disabled={submitting}
        />
        <TextField
          label="Job Title*"
          value={jobTitle}
          onChange={(e) => setJobTitle(e.target.value)}
          variant="outlined"
          fullWidth
          sx={fieldSx}
          disabled={submitting}
        />
        <TextField
          select
          label="Role*"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          fullWidth
          sx={fieldSx}
          disabled={submitting}
          SelectProps={{ MenuProps: selectMenuProps }}
        >
          {CREATE_ROLE_ORDER.map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Department*"
          value={department}
          onChange={(e) => setDepartment(e.target.value)}
          fullWidth
          sx={fieldSx}
          disabled={submitting}
          SelectProps={{ MenuProps: selectMenuProps }}
        >
          {departmentOptions.map((dept) => (
            <MenuItem key={dept.id} value={dept.id}>
              {dept.name}
            </MenuItem>
          ))}
        </TextField>

        <Box
          sx={{
            gridColumn: "1 / -1",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "flex-end",
            gap: 2,
            pt: 2.5,
            borderTop: `1px solid ${ui.border}`,
          }}
        >
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!canSubmit}
            startIcon={
              submitting ? (
                <CircularProgress size={18} sx={{ color: "inherit" }} />
              ) : null
            }
            sx={primaryButtonSx}
          >
            {submitting ? "Creating..." : "Create user"}
          </Button>
        </Box>
      </Box>
    </SectionCard>
  );
};

export default CreateUserForm;